/**
 * Craft Agent Session Viewer
 *
 * A minimal web app for viewing Craft Agent session transcripts.
 * Users can upload session JSON files or view shared sessions via URL.
 *
 * Routes:
 * - / - Upload interface
 * - /s/{id} - View shared session
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import type { StoredSession } from '@craft-agent/core'
import {
  SessionViewer,
  GenericOverlay,
  CodePreviewOverlay,
  MultiDiffPreviewOverlay,
  TerminalPreviewOverlay,
  JSONPreviewOverlay,
  DocumentFormattedMarkdownOverlay,
  extractOverlayData,
  type PlatformActions,
  type ActivityItem,
  type OverlayData,
  type FileChange,
} from '@craft-agent/ui'
import { SessionUpload } from './components/SessionUpload'
import { Header } from './components/Header'

/** Default session ID for development */
const DEV_SESSION_ID = 'tz5-13I84pwK_he'
const DEMO_SESSION: StoredSession = {
  id: 'demo-welcome',
  name: 'Welcome to OMNI Agents',
  workspaceId: 'demo',
  createdAt: Date.now(),
  lastUsedAt: Date.now(),
  tokenUsage: { inputTokens: 0, outputTokens: 0, totalTokens: 0, contextTokens: 0, costUsd: 0 },
  messages: [
    { id: 'm1', type: 'user', content: 'Show me how OMNI Agents works.' },
    {
      id: 'm2',
      type: 'assistant',
      content:
        'This is a demo transcript. Use the app switcher in the header to jump between OMNI and OMNI Agents, or upload your own session JSON below.',
    },
  ],
}

/** Extract session ID from URL path /s/{id} */
function getSessionIdFromUrl(): string | null {
  const path = window.location.pathname
  const match = path.match(/^\/s\/([a-zA-Z0-9_-]+)$/)
  if (match) return match[1]

  // In development, redirect root to default session
  if (import.meta.env.DEV && path === '/') {
    window.history.replaceState({}, '', `/s/${DEV_SESSION_ID}`)
    return DEV_SESSION_ID
  }

  return null
}

const API_BASE = (() => {
  const fromEnv = typeof import.meta !== 'undefined' ? (import.meta as any).env?.VITE_OMNI_AGENTS_API_BASE : undefined
  const candidates = [
    fromEnv,
    'https://agents-api.omnibrief.app',
    'https://omni-agents-api.arcade-group.workers.dev', // fallback for workers.dev
  ].filter(Boolean)
  return candidates[0] as string
})()

export function App() {
  const [session, setSession] = useState<StoredSession | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(() => getSessionIdFromUrl())
  const [isDark, setIsDark] = useState(() => {
    // Check system preference on mount
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })
  const [autoSessionDone, setAutoSessionDone] = useState(false)

  // Fetch session from API when we have a session ID
  useEffect(() => {
    if (!sessionId) return

    const fetchSession = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await fetch(`${API_BASE}/s/api/${sessionId}`)
        if (!response.ok) {
          if (response.status === 404) {
            setError('Session not found')
          } else {
            setError('Failed to load session')
          }
          return
        }

        const data = await response.json()
        setSession(data)
      } catch (err) {
        console.error('Failed to fetch session:', err)
        setError('Failed to load session')
      } finally {
        setIsLoading(false)
      }
    }

    fetchSession()
  }, [sessionId])

  // Auto-create a session using the Worker Anthropic proxy for single-user mode
  useEffect(() => {
    if (autoSessionDone || session || sessionId) return

    const abortController = new AbortController()

    const createAutoSession = async () => {
      setIsLoading(true)
      setError(null)
      const prompt = 'Say hello and confirm OMNI Agents is authenticated.'
      try {
        const resp = await fetch(`${API_BASE}/anthropic/v1/messages`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            model: 'claude-sonnet-4-20250514',
            max_tokens: 128,
            messages: [{ role: 'user', content: prompt }],
          }),
          signal: abortController.signal,
        })

        // Check if aborted before processing response
        if (abortController.signal.aborted) return

        if (!resp.ok) throw new Error(`Anthropic proxy error ${resp.status}`)
        const data = await resp.json()

        // Check again after async operation
        if (abortController.signal.aborted) return

        const reply =
          Array.isArray(data?.content) && data.content.length > 0
            ? data.content
                .map((c: any) => (typeof c.text === 'string' ? c.text : ''))
                .join('\n')
            : JSON.stringify(data)

        const auto: StoredSession = {
          id: `auto-${Date.now()}`,
          name: 'Auto session (Anthropic)',
          workspaceId: 'auto',
          createdAt: Date.now(),
          lastUsedAt: Date.now(),
          tokenUsage: { inputTokens: 0, outputTokens: 0, totalTokens: 0, contextTokens: 0, costUsd: 0 },
          messages: [
            { id: 'u1', type: 'user', content: prompt },
            { id: 'a1', type: 'assistant', content: reply },
          ],
        }
        setSession(auto)
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') return
        console.error('Auto session failed', err)
        if (!abortController.signal.aborted) {
          setSession(DEMO_SESSION)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setAutoSessionDone(true)
          setIsLoading(false)
        }
      }
    }

    void createAutoSession()

    return () => {
      abortController.abort()
    }
  }, [autoSessionDone, session, sessionId])

  // Handle browser navigation
  useEffect(() => {
    const handlePopState = () => {
      const newId = getSessionIdFromUrl()
      setSessionId(newId)
      if (!newId) {
        setSession(null)
        setError(null)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Apply dark mode class to html element
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)
  }, [isDark])

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches)
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  const handleSessionLoad = useCallback((loadedSession: StoredSession) => {
    setSession(loadedSession)
  }, [])

  const handleClear = useCallback(() => {
    setSession(null)
    setSessionId(null)
    setError(null)
    setAutoSessionDone(false) // Reset so auto-session can be tried again
    // Update URL to root
    window.history.pushState({}, '', '/')
  }, [])

  const toggleTheme = useCallback(() => {
    setIsDark(prev => !prev)
  }, [])

  // State for overlay
  const [overlayActivity, setOverlayActivity] = useState<ActivityItem | null>(null)
  // State for multi-diff overlay (Edit/Write activities shown as diffs)
  const [multiDiffState, setMultiDiffState] = useState<{ changes: FileChange[] } | null>(null)

  // Handle activity click - Edit/Write opens multi-diff, others use extractOverlayData
  const handleActivityClick = useCallback((activity: ActivityItem) => {
    if (activity.toolName === 'Edit' || activity.toolName === 'Write') {
      const input = activity.toolInput as Record<string, unknown> | undefined
      const filePath = (input?.file_path as string) || (input?.path as string) || 'unknown'
      const change: FileChange = {
        id: activity.id,
        filePath,
        toolType: activity.toolName,
        original: activity.toolName === 'Edit' ? ((input?.old_string as string) || '') : '',
        modified: activity.toolName === 'Edit'
          ? ((input?.new_string as string) || '')
          : ((input?.content as string) || ''),
        error: activity.error || undefined,
      }
      setMultiDiffState({ changes: [change] })
    } else {
      setOverlayActivity(activity)
    }
  }, [])

  const handleCloseOverlay = useCallback(() => {
    setOverlayActivity(null)
    setMultiDiffState(null)
  }, [])

  // Extract overlay data using shared parser (non-Edit/Write tools only)
  const overlayData: OverlayData | null = useMemo(() => {
    if (!overlayActivity) return null
    return extractOverlayData(overlayActivity)
  }, [overlayActivity])

  // Platform actions for the viewer (limited functionality)
  const platformActions: PlatformActions = {
    onOpenUrl: (url) => {
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    onCopyToClipboard: async (text) => {
      await navigator.clipboard.writeText(text)
    },
  }

  const theme = isDark ? 'dark' : 'light'

  // Single-user bootstrap: load demo session only after auto-session attempt finishes
  useEffect(() => {
    if (!autoSessionDone || session || sessionId) return
    setSession(DEMO_SESSION)
  }, [autoSessionDone, session, sessionId])

  return (
    <div className="h-full flex flex-col bg-foreground-2 text-foreground">
      <Header
        hasSession={!!session}
        sessionTitle={session?.name}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        onClear={handleClear}
      />

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center text-muted-foreground">
            <div className="animate-pulse">Loading session...</div>
          </div>
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <div className="text-destructive mb-4">{error}</div>
            <button
              onClick={handleClear}
              className="px-4 py-2 rounded-md bg-background text-foreground shadow-sm border border-border hover:bg-foreground/5 transition-colors"
            >
              Go back
            </button>
          </div>
        </div>
      ) : session ? (
        <SessionViewer
          session={session}
          mode="readonly"
          platformActions={platformActions}
          defaultExpanded={false}
          className="flex-1 min-h-0"
          onActivityClick={handleActivityClick}
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 gap-8 text-center">
          <div className="max-w-2xl space-y-3">
            <h1 className="text-2xl font-semibold text-foreground">Welcome to OMNI Agents</h1>
            <p className="text-foreground/70">
              Switch between OMNI and OMNI Agents from the header. You can view a demo transcript or upload a session JSON.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                className="px-4 py-2 rounded-md bg-foreground text-background hover:bg-foreground/90 transition"
                onClick={() => handleSessionLoad(DEMO_SESSION)}
              >
                View demo transcript
              </button>
              <button
                className="px-4 py-2 rounded-md border border-foreground/20 text-foreground hover:bg-foreground/5 transition"
                onClick={() => window.open('https://omnibrief.app', '_self')}
              >
                Go to OMNI
              </button>
            </div>
          </div>
          <div className="w-full max-w-xl">
            <SessionUpload onSessionLoad={handleSessionLoad} />
          </div>
        </div>
      )}

      {/* Code preview overlay for Read/Write tools */}
      {overlayData?.type === 'code' && (
        <CodePreviewOverlay
          isOpen={!!overlayActivity}
          onClose={handleCloseOverlay}
          content={overlayData.content}
          filePath={overlayData.filePath}
          mode={overlayData.mode}
          startLine={overlayData.startLine}
          totalLines={overlayData.totalLines}
          numLines={overlayData.numLines}
          theme={theme}
          error={overlayData.error}
        />
      )}

      {/* Multi-diff preview overlay for Edit/Write tools */}
      {multiDiffState && (
        <MultiDiffPreviewOverlay
          isOpen={true}
          onClose={handleCloseOverlay}
          changes={multiDiffState.changes}
          consolidated={false}
          theme={theme}
        />
      )}

      {/* Terminal preview overlay for Bash/Grep/Glob tools */}
      {overlayData?.type === 'terminal' && (
        <TerminalPreviewOverlay
          isOpen={!!overlayActivity}
          onClose={handleCloseOverlay}
          command={overlayData.command}
          output={overlayData.output}
          exitCode={overlayData.exitCode}
          toolType={overlayData.toolType}
          description={overlayData.description}
          theme={theme}
        />
      )}

      {/* JSON preview overlay for tools returning JSON data */}
      {overlayData?.type === 'json' && (
        <JSONPreviewOverlay
          isOpen={!!overlayActivity}
          onClose={handleCloseOverlay}
          data={overlayData.data}
          title={overlayData.title}
          theme={theme}
          error={overlayData.error}
        />
      )}

      {/* Document overlay for formatted markdown content (Write tool on .md/.txt, WebSearch results) */}
      {overlayData?.type === 'document' && (
        <DocumentFormattedMarkdownOverlay
          isOpen={!!overlayActivity}
          onClose={handleCloseOverlay}
          content={overlayData.content}
          filePath={overlayData.filePath}
          typeBadge={{ label: overlayData.toolName, variant: 'default' }}
          onOpenUrl={platformActions.onOpenUrl}
          error={overlayData.error}
        />
      )}

      {/* Generic overlay for unknown tools */}
      {overlayData?.type === 'generic' && (
        <GenericOverlay
          isOpen={!!overlayActivity}
          onClose={handleCloseOverlay}
          content={overlayData.content}
          title={overlayData.title}
          theme={theme}
        />
      )}
    </div>
  )
}
